import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { writeDashboardEntry } from "./dashboardCache";
import { PERSISTED_VIEWS, conditionalGet, dashKey, etagFor, setEtag } from "./useDashboardData";

/**
 * "Anything new?" — GET /api/dashboard/version every 20 s while the page is
 * visible (React Query skips interval fetches while the document is hidden:
 * refetchIntervalInBackground false), and again on focus. When the store
 * version moves, every dashboard query of this store is invalidated: the ones
 * on screen (active = mounted AND enabled, i.e. the visible tab) refetch now,
 * the other tabs are only marked stale and refetch when opened.
 *
 * An API without the endpoint (404 / 405) switches the poll off — the
 * per-query refetchInterval fallback still keeps every tab current.
 */

export const VERSION_INTERVAL = 20 * 1000;
const ABSENT = new Set([404, 405, 501]);

// /version's ETag is derivable: "dv-<store_id>-<store_version>" (quotes included).
export const versionEtag = (d) => (d && d.store_id != null && d.store_version != null ? `"dv-${d.store_id}-${d.store_version}"` : null);

export const versionOf = (d) => (d && typeof d === "object" ? d.store_version ?? d.version ?? d.changed_at ?? null : null);

export const isStoreDashboardKey = (key, storeKey) =>
  Array.isArray(key) && key[0] === "dashboard" && key[2] === (storeKey ?? "home") && PERSISTED_VIEWS.includes(key[1]) && key[1] !== "me" && key[1] !== "settings";

export function useDashboardFreshness({ storeKey, userId, enabled }) {
  const qc = useQueryClient();
  const key = dashKey("version", storeKey);
  const q = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => (await conditionalGet("/api/dashboard/version", undefined, { signal, userId, key, getCurrent: () => qc.getQueryData(key), etagOf: versionEtag })).data ?? null,
    enabled,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchIntervalInBackground: false,
    refetchInterval: (_data, query) => (ABSENT.has(query?.state?.error?.response?.status) ? false : VERSION_INTERVAL),
  });

  const version = versionOf(q.data);
  const seen = useRef(null);
  useEffect(() => {
    seen.current = null;
  }, [storeKey]);
  useEffect(() => {
    if (version == null) return;
    const prev = seen.current;
    seen.current = version;
    if (prev == null || String(prev) === String(version)) return;
    // cancelRefetch false: a part still polling its 202 keeps going instead of restarting.
    qc.invalidateQueries({ predicate: (query) => isStoreDashboardKey(query.queryKey, storeKey) }, { cancelRefetch: false });
  }, [version, qc, storeKey]);

  return { version, available: !ABSENT.has(q.error?.response?.status) };
}

/**
 * Persist every dashboard result (fetched OR written optimistically with
 * setQueryData) to the user's own storage copy. A manual write no longer
 * matches the server's ETag, so the tag is dropped with it.
 */
export function useDashboardPersistence(userId) {
  const qc = useQueryClient();
  useEffect(() => {
    if (userId == null) return undefined;
    const cache = qc.getQueryCache();
    return cache.subscribe((event) => {
      if (event?.type !== "updated" || event.action?.type !== "success") return;
      const query = event.query;
      const key = query?.queryKey;
      if (!Array.isArray(key) || key[0] !== "dashboard" || !PERSISTED_VIEWS.includes(key[1])) return;
      const data = query.state.data;
      if (data === undefined || data?.status === "computing") return;
      if (event.action.manual) setEtag(userId, key, null);
      writeDashboardEntry(userId, key, data, {
        updatedAt: query.state.dataUpdatedAt || Date.now(),
        etag: event.action.manual ? null : etagFor(userId, key),
      });
    });
  }, [qc, userId]);
}
